import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Repeat, ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";
import { api, SITE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SteamLoginButton from "@/components/SteamLoginButton";

export default function BoutiquePage() {
  const { user } = useAuth();
  const [loadingId, setLoadingId] = useState(null);
  const [selectedCat, setSelectedCat] = useState("all");
  const [promoCode, setPromoCode] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["shop-items"],
    queryFn: () => api.get("/shop/items").then((r) => r.data),
  });
  const { data: categories = [] } = useQuery({
    queryKey: ["shop-categories"],
    queryFn: () => api.get("/shop/categories").then((r) => r.data),
  });

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    try {
      const { data } = await api.post("/shop/promos/validate", { code: promoCode });
      setPromoInfo(data);
      toast.success(`Code ${data.code} accepté : ${data.percent_off ? `-${data.percent_off}%` : `-${data.amount_off_eur} €`}`);
    } catch (e) {
      setPromoInfo(null);
      toast.error(e?.response?.data?.detail || "Code invalide");
    }
  };

  const buy = async (item) => {
    if (!user) return toast.error("Connectez-vous avec Steam pour acheter.");
    setLoadingId(item.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        item_id: item.id,
        origin_url: window.location.origin,
        promo_code: promoInfo ? promoInfo.code : null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setLoadingId(null);
    }
  };

  const filtered = selectedCat === "all" ? items : items.filter((i) => (i.category_slug || "none") === selectedCat);

  // Group items by category when "Tout" is selected, keeping the categories order from API
  const groupedSections = (() => {
    if (selectedCat !== "all") return null;
    const map = new Map();
    for (const it of items) {
      const slug = it.category_slug || "none";
      if (!map.has(slug)) map.set(slug, []);
      map.get(slug).push(it);
    }
    const sections = [];
    for (const c of categories) {
      const list = map.get(c.slug);
      if (list && list.length) sections.push({ slug: c.slug, name: c.name, items: list });
    }
    const uncat = map.get("none");
    if (uncat && uncat.length) sections.push({ slug: "none", name: "Autres", items: uncat });
    return sections;
  })();

  const renderItem = (item) => (
    <Card key={item.id} className="overflow-hidden group hover:border-primary/60 transition-colors flex flex-col" data-testid={`shop-item-${item.id}`}>
      <div className="aspect-video bg-secondary relative overflow-hidden">
        {item.image_url ? (
          <img src={item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ShoppingBag className="h-10 w-10" />
          </div>
        )}
        {item.kind === "subscription" && (
          <Badge className="absolute top-3 right-3 bg-primary text-primary-foreground">
            <Repeat className="h-3 w-3 mr-1" /> Abonnement
          </Badge>
        )}
      </div>
      <CardContent className="p-5 flex-1 flex flex-col">
        <h3 className="font-display text-xl uppercase tracking-tight mb-1">{item.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-3 mb-4 flex-1">{item.description}</p>
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="font-display text-3xl font-black text-primary" data-testid={`shop-price-${item.id}`}>
              {item.price_eur.toFixed(2)} €
            </p>
            {item.kind === "subscription" && (
              <p className="text-xs text-muted-foreground font-mono-tech">/ {item.interval === "year" ? "an" : "mois"}</p>
            )}
          </div>
          <Button onClick={() => buy(item)} disabled={loadingId === item.id} data-testid={`buy-${item.id}`}>
            {loadingId === item.id ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Redirection…</> : "Acheter"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <header className="mb-8">
        <Badge variant="outline" className="border-primary/40 text-primary font-mono-tech uppercase tracking-widest mb-3">
          <ShoppingBag className="h-3 w-3 mr-1" /> Boutique officielle
        </Badge>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight">Soutenir RiverSide</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Vos achats sont livrés directement en jeu sur le pseudonyme de votre compte Steam connecté. Paiement sécurisé par Stripe.
        </p>
      </header>

      {!user && (
        <Card className="mb-6 border-primary/40">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm">Connectez-vous avec Steam pour que vos achats soient livrés à votre personnage en jeu.</p>
            <SteamLoginButton />
          </CardContent>
        </Card>
      )}

      {/* Category filter */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => setSelectedCat("all")} className={`px-3 py-1.5 rounded-full text-sm border ${selectedCat === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`} data-testid="cat-filter-all">Tout</button>
          {categories.map((c) => (
            <button key={c.slug} onClick={() => setSelectedCat(c.slug)} className={`px-3 py-1.5 rounded-full text-sm border ${selectedCat === c.slug ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`} data-testid={`cat-filter-${c.slug}`}>{c.name}</button>
          ))}
        </div>
      )}

      {/* Promo code */}
      {user && (
        <Card className="mb-6">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <p className="text-sm font-medium">Code promo :</p>
            <input
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="CODE"
              className="h-9 px-3 rounded-md border border-input bg-background text-sm font-mono-tech uppercase w-40"
              data-testid="promo-input"
            />
            <Button size="sm" onClick={applyPromo} data-testid="promo-apply">Appliquer</Button>
            {promoInfo && (
              <Badge variant="outline" className="border-primary/50 text-primary" data-testid="promo-applied">
                {promoInfo.code} appliqué : {promoInfo.percent_off ? `-${promoInfo.percent_off}%` : `-${promoInfo.amount_off_eur} €`}
                <button onClick={() => { setPromoInfo(null); setPromoCode(""); }} className="ml-2 text-destructive">×</button>
              </Badge>
            )}
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Chargement...</p>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground">
          Aucun article dans cette catégorie pour le moment.
        </CardContent></Card>
      ) : groupedSections ? (
        <div className="space-y-12">
          {groupedSections.map((section) => (
            <section key={section.slug} data-testid={`shop-section-${section.slug}`}>
              <div className="flex items-center gap-3 mb-5">
                <h2 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight">
                  {section.name}
                </h2>
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs font-mono-tech text-muted-foreground uppercase tracking-widest">
                  {section.items.length} article{section.items.length > 1 ? "s" : ""}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {section.items.map(renderItem)}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(renderItem)}
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-8 max-w-2xl">
        Paiements traités par Stripe. Vos coordonnées bancaires ne transitent jamais par RiverSide. Les avantages sont livrés sur le SteamID64 du compte connecté au moment de l'achat. En cas de problème, contactez le staff via <Link to="/tickets/nouveau" className="text-primary underline">un ticket</Link>.
      </p>
    </div>
  );
}
